import { useEffect, useRef, useState } from 'react'
import { isUndefined, range } from 'lodash'
import moment from 'moment'
import { GridColDef } from '@microservices/wiskey-react-table-component'
import { GridSortItem } from '@mui/x-data-grid'

import { ActualTimeWindowInterval, useAppDispatch } from '@hooks'
import {
  filterDataByTimeInterval,
  generateDateInterval,
  getSortParamRequest,
  sortObjectData,
} from '@helpers'
import { SIZE_PAGE } from '@constants'
import { ObjectDataRecord, POSTObjectDataFilter, UseMutationType } from '@types'

export type ListQueryResponse<T> = {
  data: T[]
  totalCount: number
  totalCountElements: number
}

type FilterParam = string | undefined

export type SortType = {
  sortKey?: string
} & GridSortItem

export type ScrollingParams = {
  sort?: SortType[]
  filter?: FilterParam
  page: number
} & Record<string, unknown>

type QueryParameters = {
  size?: string
  skip?: boolean
} & Record<string, unknown>

export const useInfiniteScroll = <Payload extends Record<string, unknown>, Return>(
  useGetDataList: UseMutationType<Payload, ListQueryResponse<Return>>,
  {
    size = SIZE_PAGE,
    skip: skipParam = false,
    onInitialLoad,
    ...outerQueryParameters
  }: QueryParameters &
    Payload & {
      onInitialLoad?: (data: ListQueryResponse<Return>) => void
    },
  transformResponse?: (data: Return[]) => Return[],
  defaultScrollingParams?: Partial<ScrollingParams>,
  // TODO Скорее всего это костыль, так как хук не должен знать про колонки (наверное)
  // В любом случае, это должно будет уйти при переходе на загрузку данных напрямую через Core
  columns?: GridColDef<ObjectDataRecord>[],
  autoFetchOnMount = true // TODO Рефакторинг параметров в хуке
) => {
  const [skip, setSkip] = useState(skipParam)
  const [combinedData, setCombinedData] = useState<Return[]>([])
  const [isActiveTimeWindow, setActiveTimeWindow] = useState(false)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCountElements, setTotalCountElements] = useState(0)
  const [oldTimeWindow, setOldTimeWindow] = useState<ActualTimeWindowInterval | undefined>()

  const [loadingAllPages, setLoadingAllPages] = useState(false)

  const dispatch = useAppDispatch()
  const latestRequestIdRef = useRef(0)

  const [scrollingParams, setScrollingParams] = useState<ScrollingParams>({
    page: 0,
    sort: undefined,
    filter: undefined,
    ...defaultScrollingParams,
  })

  useEffect(() => {
    setScrollingParams(prev => ({ ...prev, size }))
  }, [size])

  useEffect(() => {
    setSkip(Boolean(skipParam))
  }, [skipParam])

  const [getDataList, { data: listData, isLoading, isError, isUninitialized }] = useGetDataList()

  const changePageElementsParams = (totalPages: number, totalCountElements: number) => {
    setTotalPages(totalPages)
    setTotalCountElements(totalCountElements)
  }

  const refresh = (
    params?: Omit<ScrollingParams, 'page'>,
    _skip?: boolean,
    timeWindow?: ActualTimeWindowInterval
  ): void => {
    if (_skip !== undefined) {
      setSkip(_skip)
    }

    if (skip) {
      return
    }

    fetchDataList({ page: 0, ...params }, undefined, skip, timeWindow)
  }

  const changeSort = (sort: SortType[], params?: Record<string, unknown>) => {
    fetchDataList({ ...scrollingParams, ...params, page: 0, sort })
  }

  const showError = (err: unknown) => {
    setLoadingAllPages(false)
  }

  const loadPage =
    (newScrollingParams: ScrollingParams, currentRequestId: number) => (res: unknown) => {
      if (currentRequestId !== latestRequestIdRef.current) {
        return
      }
      const { data, totalCountElements, totalCount } = res as ListQueryResponse<Return>

      const nextData = transformResponse ? transformResponse(data) : data

      if (data) {
        changePageElementsParams(totalCount || 1, totalCountElements)
      }
      // TODO Костыль. Иначе после сортировки падает ошибка No row with id # found
      setTimeout(() => {
        if (newScrollingParams.page === 0) {
          setCombinedData(nextData)
        } else {
          setCombinedData(previousData => [...previousData, ...nextData])
        }
      }, 0)

      return res
    }

  const loadAllPagesInTimeInterval =
    (params: Payload) =>
    (firstPage: ListQueryResponse<Return>): Promise<ListQueryResponse<Return>> => {
      return new Promise((resolve, reject) => {
        const { data: firstPageData } = firstPage

        const newFirstPageData = transformResponse
          ? transformResponse(firstPageData)
          : firstPageData

        if (firstPage.totalCount <= 1) {
          const firstPageTotalCount = firstPage.totalCount || 1
          resolve({
            data: newFirstPageData,
            totalCount: firstPageTotalCount,
            totalCountElements: 0, // Количество не требуется, оно определяется на основе размера данных в следующей цепочке промиса
          })

          return
        }

        // Грузим данные для всех страниц асинхронно
        const requests = range(1, firstPage.totalCount).map(page =>
          getDataList({ ...params, page }).unwrap()
        )

        Promise.all(requests)
          .then(res => {
            const lastPage = (res[res.length - 1] as unknown as { currentPage: number }).currentPage

            setScrollingParams(prev => ({
              ...prev,
              page: lastPage,
            }))

            const otherData = res.map(item => item.data).flat(1)

            const newOtherData = transformResponse
              ? transformResponse([...newFirstPageData, ...otherData])
              : [...newFirstPageData, ...otherData]

            const lastPageTotalCount = res.at(-1)?.totalCount || 1
            resolve({
              data: newOtherData,
              totalCount: lastPageTotalCount, // Берём количество страниц из данных последней загруженной страницы
              totalCountElements: 0, // Количество не требуется, оно определяется на основе размера данных в следующей цепочке промиса
            })
          })
          .catch(reject)
      })
    }

  const handleLoadingAllPagesInTimeInterval =
    (newTimeWindow: ActualTimeWindowInterval | undefined, params: Payload) =>
    (res: ListQueryResponse<Return>) => {
      const needPartialUpdate = newTimeWindow?.isFirstIntervalRun === false

      const { data: newData, totalCount } = res as ListQueryResponse<Return>
      const realDataField = newTimeWindow?.field

      // При смещении временного окна мы не грузим данные заново полностью. Мы загружаем только новую часть
      if (needPartialUpdate && realDataField) {
        const timeInterval = generateDateInterval(newTimeWindow?.interval, newTimeWindow?.timeUnit)
        // Старые данные, которые не подходят под фильтры нового временного окна, фильтруются
        const filteredDataByTimeInterval = filterDataByTimeInterval(
          combinedData,
          timeInterval,
          realDataField as keyof Return
        )

        // Соединяем новые данные за смещённое временное окно с старыми отфильтрованными данными
        const oldAndNewData =
          params?.sort?.length > 0
            ? // Сортируем данные после дозагрузки новой порции (так как новую порцию данных нужно распределить по нужным местам)
              sortObjectData(
                [...newData, ...filteredDataByTimeInterval] as ObjectDataRecord[],
                params.sort
              )
            : // При отсутствии сортировки просто вставляем данные сверху (На такое решение дал апрув архитектор)
              // *Может отличаться от дефолтной сортировки на бэке и визуально может меняться порядок после загрузки новой порции*
              [...newData, ...filteredDataByTimeInterval]

        changePageElementsParams(totalCount, oldAndNewData.length)
        setCombinedData(oldAndNewData)

        return
      }

      changePageElementsParams(totalCount, newData.length)
      // Сетим новые данные целиком, в случае с полной перезагрузкой
      setCombinedData(newData)
    }

  const updateParamsByTimeWindow = (
    params: Payload,
    newTimeWindow?: ActualTimeWindowInterval // TODO Думаю этот хук вообще про "body" для ассистента поиска знать не должен, но пока это работает так
  ): Payload => {
    const body = (params as unknown as { body: POSTObjectDataFilter }).body || []

    const timeWindowBodyItemIndex = body.findIndex(item => item.timeWindow)

    if (timeWindowBodyItemIndex === -1 || !newTimeWindow?.interval) {
      return params
    }

    const newBody = [...body]
    newBody[timeWindowBodyItemIndex].value = { JSONValue: newTimeWindow.interval }

    return {
      ...params,
      body: newBody,
    }
  }

  const fetchDataList = (
    newScrollingParams: ScrollingParams,
    newOuterParams?: Record<string, unknown>,
    newSkip?: boolean,
    newTimeWindow?: ActualTimeWindowInterval
  ): Promise<ListQueryResponse<Return> | undefined> => {
    return new Promise((resolve, reject) => {
      if (!skip || !newSkip) {
        const currentRequestId = ++latestRequestIdRef.current
        setScrollingParams(prev => ({ ...prev, ...newScrollingParams }))
        const outerParams = newOuterParams ? newOuterParams : outerQueryParameters
        const params = {
          size,
          ...outerParams,
          ...newScrollingParams,
          sort: getSortParamRequest(
            'sort' in newScrollingParams ? newScrollingParams.sort : scrollingParams.sort
          ),
          filter:
            'filter' in newScrollingParams ? newScrollingParams.filter : scrollingParams.filter,
        } as unknown as Payload

        const handleSuccess = (res: any) => {
          if (currentRequestId !== latestRequestIdRef.current) {
            return
          }
          // Если это первая загрузка (page === 0) и передан onInitialLoad
          if (newScrollingParams.page === 0 && onInitialLoad) {
            onInitialLoad(res)
          }
          resolve(res)
        }

        // Каждый раз при обновлении окна == true
        const hasNewTimeWindow = Boolean(newTimeWindow?.interval)
        const hasActiveOldTimeWindow = isActiveTimeWindow && isUndefined(newTimeWindow?.interval)
        const fetchAllPages =
          hasNewTimeWindow || // Если передали новое значение временного окна
          hasActiveOldTimeWindow // Если новое окно не передавали, но старое временное окно активно

        // Загрузка данных за определённое временное окно
        if (fetchAllPages) {
          const timeWindowParams = updateParamsByTimeWindow(params, newTimeWindow)

          // Сохраняем предыдущее значение перед обновлением
          if (newTimeWindow) {
            setOldTimeWindow(newTimeWindow)
          }

          setActiveTimeWindow(true)

          const needPartialUpdate = newTimeWindow?.isFirstIntervalRun === false

          // TODO Должно быть как-то иначе (возможно единый механизм генерации rsql после переезда чисто на команды Core) + склеиваться с уже переданным в параметры фильтром
          const endOffset = newTimeWindow?.interval?.[2]
          const unit = newTimeWindow?.timeUnit.toLowerCase()
          const oldStartInterval = moment(oldTimeWindow?.interval?.[0])
            .add(Number(endOffset), unit)
            .toISOString()
          const newStartInterval = moment(newTimeWindow?.interval?.[0])
            .add(Number(endOffset), unit)
            .toISOString()
          const partialTimeIntervalFilter = [
            `(${oldTimeWindow?.field}=gt=${oldStartInterval})`,
            `(${newTimeWindow?.field}=le=${newStartInterval})`,
          ].join(';')

          getDataList(
            needPartialUpdate
              ? { ...timeWindowParams, filter: partialTimeIntervalFilter }
              : {
                  ...timeWindowParams,
                }
          )
            .unwrap()
            .then(loadAllPagesInTimeInterval(timeWindowParams))
            .then(handleLoadingAllPagesInTimeInterval(newTimeWindow, params))
            .then(handleSuccess)
            .catch(error => {
              showError(error)
              reject(error)
            })

          return
        }

        setActiveTimeWindow(false)

        // Загрузка данных только за определённую страницу
        getDataList({
          ...params,
        })
          .unwrap()
          .then(loadPage(newScrollingParams, currentRequestId))
          .then(handleSuccess)
          .catch(error => {
            showError(error)
            reject(error)
          })
      } else {
        resolve(undefined)
      }
    })
  }

  // TODO Костыль, который требует переработки. Начинает мешать везде
  // Первый вызов fetchDataList
  useEffect(() => {
    if (!autoFetchOnMount) return

    if (skipParam) return
    fetchDataList({ page: 0, ...outerQueryParameters })
  }, [skipParam])

  const readMore = (params?: ScrollingParams): Promise<ListQueryResponse<Return> | undefined> => {
    return new Promise((resolve, reject) => {
      if (scrollingParams.page + 1 < totalPages && !skip) {
        fetchDataList({
          ...scrollingParams,
          ...params,
          page: scrollingParams.page + 1,
        })
          .then(resolve)
          .catch(reject)
      } else {
        resolve(undefined) // или можно reject(new Error('No more pages or skipped'))
      }
    })
  }

  const handleSetParams = (params: ScrollingParams) => {
    setScrollingParams(params)
    fetchDataList(params)
  }

  const refetch = (outerParams?: Record<string, unknown>) => fetchDataList({ page: 0 }, outerParams)

  const refreshAllPages = (outerParams?: Record<string, unknown>) => {
    const params = {
      size,
      ...outerQueryParameters,
      ...scrollingParams,
      ...outerParams,
      filter: outerParams?.filter || scrollingParams.filter || outerQueryParameters?.filter,
      sort: getSortParamRequest(scrollingParams.sort),
    } as Payload

    setLoadingAllPages(true)

    // Если активно временное окно — обновляем данные через time-window пайплайн,
    // чтобы также применялась логика handleLoadingAllPagesInTimeInterval
    if (isActiveTimeWindow) {
      const timeWindowParams = updateParamsByTimeWindow(params, oldTimeWindow)

      return (
        getDataList({ ...timeWindowParams, page: 0 })
          .unwrap()
          .then(loadAllPagesInTimeInterval(timeWindowParams))
          // Передаём undefined в newTimeWindow, чтобы выполнить полную замену данных (не частичную)
          .then(handleLoadingAllPagesInTimeInterval(undefined, params))
          .then(res => {
            setLoadingAllPages(false)

            return res
          })
          .catch(err => {
            showError(err)
            throw err
          })
      )
    }

    const requests = range(0, scrollingParams.page + 1).map(page =>
      getDataList({ ...params, page }).unwrap()
    )

    return Promise.all(requests)
      .then(res => {
        if (res.length) {
          const { totalCount, totalCountElements } = res[0] as ListQueryResponse<Return>
          changePageElementsParams(totalCount || 1, totalCountElements)
        }
        const otherData = res.map(item => item.data).flat(1)

        const newOtherData = transformResponse ? transformResponse([...otherData]) : [...otherData]
        setCombinedData(newOtherData)
        setLoadingAllPages(false)

        return res
      })
      .catch(err => {
        showError(err)
        throw err
      })
  }

  return {
    combinedData,
    currentPage: scrollingParams.page,
    currentSort: scrollingParams.sort,
    currentFilter: scrollingParams.filter,
    scrollingParams,
    totalPages,
    totalCountElements,
    refresh,
    refetch,
    readMore,
    setCombinedDataForce: setCombinedData,
    onSetParams: handleSetParams,
    onChangeSort: changeSort,
    isLoading: isLoading || loadingAllPages,
    isError,
    isUninitialized,
    refreshAllPages,
    isActiveTimeWindow,
  }
}
